export const LEGACY_REVENUE_ROUTES = {
  PROFILE_REVENUE: '/provider/profile/revenue',
};

export type RegisterFlow =
  | 'medicalcare_plus_therapy'
  | 'medicalcare'
  | 'new_therapy'
  | 'zocdoc'
  | 'zd';

export type CheckoutFlow =
  | 'uninsured'
  | 'mindfulness'
  | 'together'
  | 'therapy'
  | 'medicalcare_plus_therapy'
  | 'medicalcare'
  | 'new_therapy';

export type ThanksFlow = CheckoutFlow | 'scale';

export const REGISTER_FLOWS =
  '(medicalcare_plus_therapy|medicalcare|new_therapy|zocdoc|zd)';

export const CHECKOUT_FLOWS =
  '(uninsured|mindfulness|together|therapy|medicalcare_plus_therapy|medicalcare|new_therapy)';

export const THANKS_FLOWS =
  '(uninsured|mindfulness|together|therapy|medicalcare_plus_therapy|medicalcare|new_therapy|scale)';

export const REGISTER_FLOW_ROUTES = (flow: RegisterFlow) => `/${flow}/register`;
export const THANK_YOU_ROUTES = (flow: ThanksFlow) => `/${flow}/thanks`;
export const REGISTER_STRIPE_ROUTES = (flow: CheckoutFlow) =>
  `/${flow}/register/stripe`;
export const RESUBSCRIBE_CHECKOUT_ROUTES = (flow: CheckoutFlow) =>
  `/${flow}/resubscribe/stripe`;

export const MINOR_CONSENT_ROUTES = (patientId: string) =>
  `/minor-consent/${patientId}`;
export const SELF_AWARENESS_ROUTES = (patientId: string) =>
  `/self-awareness/${patientId}`;
export const SHORTS_ROUTES = (audioId: string) => `/shorts/${audioId}`;
export const AUDIO_ROUTES = (providerId: string, audioId: string) =>
  `/audio/${providerId}/${audioId}`;
export const POST_ROUTES = (slug: string) => `/post/${slug}`;
export const INTAKE_LOGIN_ROUTES = (intakeType: string) =>
  `/intake-login/${intakeType}`;
export const PRACTICE_REGISTER_ROUTES = (providerId: string) =>
  `/practice/register/${providerId}`;
export const DIRECT_BOOKING_PROVIDER_ROUTES = (userId: string) =>
  `/direct-booking/provider/${userId}`;
export const ONBOARDING_LOBBY_ROUTES = (intakeType: string) =>
  `/onboarding-lobby/${intakeType}`;
export const CALENDAR_MODE_ROUTES = (mode: string, date: string) =>
  `/provider/calendar/${mode}/${date}`;
export const AUDIOS_ROUTES = (providerId: string) => `/audios/${providerId}`;
export const AUDIO_GENRES_ROUTES = (providerId: string, genre: string) =>
  `/audios/${providerId}/genres/${genre}`;

export const ROUTES = {
  HOME: '/',
  LOGIN: '/login',
  REFERRAL: '/referral',
  FORGOT_PASSWORD: '/forgot-password',
  RESET_PASSWORD_TOKEN: '/reset/:token',
  RESET_PASSWORD: '/reset-password',
  GET_STARTED: '/get-started',
  PLANS: '/plans',
  BASIC_REGISTER: '/basic/register',
  UNINSURED_REGISTER: '/uninsured/register',
  REGISTER_FLOW: `/${REGISTER_FLOWS}/register`,
  PRACTICE_REGISTER: PRACTICE_REGISTER_ROUTES(':providerId'),
  SELF_AWARENESS: SELF_AWARENESS_ROUTES(':memberId'),
  MINOR_CONSENT: MINOR_CONSENT_ROUTES(':memberId'),
  INTAKE_LOGIN: INTAKE_LOGIN_ROUTES(':intakeType'),
  UNSUBSCRIBE_ONBOARDING_REMINDER: '/unsubscribe/onboarding-reminder',
  POST: POST_ROUTES(':slug'),
  AUDIO: AUDIO_ROUTES(':providerId', ':audioId'),
  SHORTS: SHORTS_ROUTES(':audioId'),

  RESUBSCRIBE_PLAN: '/resubscribe-plan',
  RESUBSCRIBE_CHECKOUT: `/${CHECKOUT_FLOWS}/resubscribe/stripe`,
  REGISTER_STRIPE: `/${CHECKOUT_FLOWS}/register/stripe`,
  THANKS: `/${THANKS_FLOWS}/thanks`,

  DASHBOARD: '/dashboard',
  VIDEO_CALL: '/video-call/embed/wb',
  TERMS: '/terms-and-conditions',
  CALENDAR: '/calendar',
  MESSAGE: '/message',

  MEMBER: {
    SHARE: '/share',
    PROFILE: '/profile',
    ACCOUNT: '/account',
    DETAILS: '/member/:patientId',
    ROOT: '/member',
    MEMBERSHIP: '/membership',
    PAYMENT: '/payment',
    RECEIPTS: '/receipts',
    PROGRESS: '/progress',
    PROGRESS_UPDATE: '/progress/update',
    SELF_AWARENESS: '/self-awareness',
    ONBOARDING_LOBBY: ONBOARDING_LOBBY_ROUTES(':intakeType'),
    ONBOARDING_MEDICAL_INTAKE: '/onboarding-medical-intake',
    ONBOARDING_THERAPY_INTAKE: '/onboarding-therapy-intake',
    INTAKE_POPUP: '/intake/pop-up-partial',
  },

  APPOINTMENT: {
    ROOT: '/book-appointment',
    BOOK: '/book-appointment/:appointmentType',
    RESCHEDULE: '/reschedule-appointment/:appointmentType/:providerId',
  },

  PAYMENT: {
    ADD_CREDIT_CARD: '/add-credit-card',
    CANCEL_SUBSCRIPTION: '/cancel-subscription',
    UPDATE_PLAN: '/update-plan',
    ONBOARDING_ONE_TIME_PAYMENT: '/onboarding-one-time-payment',
    TOGETHER_ONE_TIME_PAYMENT: '/together/one-time-payment',
    PAYSTACK_VERIFY_CARD: '/paystack/verify-card',
  },

  NOTIFICATIONS: {
    LIST: '/notifications',
    SINGLE: '/notifications/:notificationId',
  },

  DIGITAL_PRACTICE: {
    ROOT: '/digital-practice',
    OPTIONS: '/digital-practice/:providerId',
  },

  PROVIDER: {
    REGISTRATION: '/provider/registration',
    REGISTRATION_THANK_YOU: '/provider/registration/thankyou',
    MEMBERS: '/provider/view-members',
    PROFILE: '/provider/profile',
    PROFILE_DETAILS: '/provider/profile/details',
    PROFILE_IN_NETWORK: '/provider/profile/in-network',
    PROFILE_SETTINGS: '/provider/profile/settings',
    PROFILE_RECEIPTS: '/provider/profile/receipts',
    PROFILE_RESOURCE_CENTER: '/provider/profile/resource-center',
    CALENDAR: '/provider/calendar',
    CALENDAR_MODE: CALENDAR_MODE_ROUTES(':mode', ':date'),
    AVAILABILITY: '/provider/availability',
    MESSAGE: '/provider/message',
    MARKETING: '/provider/marketing',
    REGISTRATION_VERIFICATION: '/provider/registration/verification-form',
    REGISTRATION_CAQH: '/provider/registration/caqh-form',
    REGISTRATION_ONBOARDING: '/provider/registration/onboarding',
    REGISTRATION_PROFILE: '/provider/registration/profile-form',
    REGISTRATION_ONBOARDING_FORM: '/provider/registration/onboarding-form',
    INTERNATIONAL_REGISTRATION: '/provider/international/registration',
    INTERNATIONAL_REGISTRATION_PROOF_OF_ID:
      '/provider/international/registration/proof-of-id',
    INTERNATIONAL_REGISTRATION_TERMS:
      '/provider/international/registration/terms',
    DIGITAL_PRACTICE: {
      ROOT: '/provider/digital-practice',
      MARKETING: '/provider/digital-practice-marketing',
      EARNINGS: '/provider/digital-practice/earnings',
      COLLABORATION: '/provider/digital-practice/collaboration',
      IN_NETWORK: '/provider/digital-practice/in-network',
      COLLABORATION_CONGRATS:
        '/provider/digital-practice/collaboration-congrats',
    },
    PAYPAL: {
      SYNC_SELLER_INFO: '/provider/sync-paypal-seller-info',
    },

    REVENUE: {
      ROOT: '/provider/revenue',
      SINGLE_SESSIONS: '/provider/revenue/single-sessions',
      GROUP_SESSIONS: '/provider/revenue/group-sessions',
      DIGITAL_PRACTICE: '/provider/revenue/digital-practice',
    },
  },

  STRIPE: {
    REAUTH: '/stripe-connect/re-auth',
    ONBOARDED: '/stripe-connect/onboarded',
  },

  EXPERIENCES: {
    ROOT: '/experiences',
    COACH: '/experiences/coach',
    CONTENT: '/experiences/content',
    AUDIOS: {
      ROOT: '/experiences/audios',
      LAUNCH: '/experiences/audios/launch',
      LIBRARY: '/experiences/audios/library',
      CLONE: '/experiences/audios/clone',
      GENERATE_SHORTS: '/experiences/audios/generate-shorts',
      TERMS: '/wellnite-audios-terms-of-use',
    },
    ART: {
      GALLERY: '/experiences/gallery',
      TERMS: '/wellnite-art-terms-of-use',
    },
  },

  DIRECT_BOOKING: {
    GET_STARTED: '/direct-booking/get-started',
    SEARCH_PROVIDERS: '/direct-booking/search-providers',
    PROVIDERS: '/direct-booking/providers',
    PROVIDER: DIRECT_BOOKING_PROVIDER_ROUTES(':userId'),
    ROOT: '/direct-booking',
  },
  AUDIOS: {
    ROOT: AUDIOS_ROUTES(':providerId'),
    GENRES: AUDIO_GENRES_ROUTES(':providerId', ':genre'),
  },
};
